import React, { useCallback, useMemo, useState } from 'react'

import { SStyled, useSTheme } from 'styles/theme'
import SBox from 'components/opinionless/SBox'
import { BoxProps } from 'rebass'

import LanguageSwitcher from 'components/LanguageSwitcher'

import SlidingSideBar from '../SlidingSideBar'

import { useTranslation } from 'i18n'
import MobileTopBar from 'components/MobileTopBar'

export enum SPageType {
    Authed = 'Authed',
    Unauthed = 'Unauthed',
    UnauthedHelp = 'UnauthedHelp',
    UnauthedHelpWithSidebar = 'UnauthedHelpWithSidebar',
    None = 'None',
    Wizard = 'Wizard',
}

interface Props extends BoxProps {
    children?: React.ReactNode
    pageType: SPageType
    language?: string
}

const Container = SStyled(SBox)`
    flex: 1 1 auto;
    min-height: 100%;
    width: 100%;
    align-items: stretch;
    display: flex;
    flex-direction: row;
`

const Content = SStyled(SBox)`
    flex: 1 1 0;
    width: 100%;
    /* min-height: fit-content; */
    display: flex;
    flex-direction: column;
`

const SPage: React.FC<Props> = (props: Props) => {
    const { pageType, children, sx = {}, ...boxProps } = props
    const theme = useSTheme()
    const { t } = useTranslation(window.LANG_NS)

    const [sideBarIsOpen, setSideBarIsOpen] = useState(false)
    const isLongBeachApplyFlow = useMemo(() => {
        return window.PROGRAM.name.toLowerCase().includes('long') && window.PROGRAM.name.toLowerCase().includes('beach')
    }, [window])

    const toggleSideBarIsOpen = useCallback(() => {
        setSideBarIsOpen(!sideBarIsOpen)
    }, [sideBarIsOpen, setSideBarIsOpen])

    const contentBg = useMemo(() => {
        if (
            pageType === SPageType.Authed ||
            pageType === SPageType.UnauthedHelp ||
            pageType === SPageType.UnauthedHelpWithSidebar
        ) {
            return 'gray100'
        } else if (pageType === SPageType.Unauthed) {
            return 'white'
        } else {
            return null
        }
    }, [pageType])

    const px = useMemo(() => {
        if (
            pageType === SPageType.Authed ||
            pageType === SPageType.UnauthedHelp ||
            pageType === SPageType.UnauthedHelpWithSidebar
        ) {
            return [0, 8, '80px']
        } else {
            return [6, 8, '80px']
        }
    }, [pageType])

    const py = useMemo(() => {
        if (
            pageType === SPageType.Authed ||
            pageType === SPageType.UnauthedHelp ||
            pageType === SPageType.UnauthedHelpWithSidebar
        ) {
            return isLongBeachApplyFlow ? ['18px', 9, '90px'] : [0, 9, '90px']
        } else {
            return isLongBeachApplyFlow ? ['18px', 9, '90px'] : [8, 9, '90px']
        }
    }, [pageType])

    return (
        <Container>
            <SlidingSideBar isOpen={sideBarIsOpen} onClose={setSideBarIsOpen} pageType={pageType} />
            <Content id="page-content" style={{ position: 'relative' }}>
                <MobileTopBar onClickHamburger={toggleSideBarIsOpen} pageType={pageType} />
                <LanguageSwitcher
                    switcherId={'desktop'}
                    display={['none', 'block', 'block']}
                    isAuthed={pageType === SPageType.Authed}
                    defaultLanguage={props.language}
                />
                <SBox
                    sx={{
                        flex: '1 1 0',
                        px: px,
                        py: py,
                        bg: contentBg,
                        minHeight: 'fit-content',
                        ...sx,
                    }}
                    {...boxProps}
                >
                    {children}
                </SBox>
            </Content>
        </Container>
    )
}

export default SPage
